import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { catchError, Subscription, throwError } from 'rxjs';
import { ChartComponent } from 'ng-apexcharts';
import { ChartConfigService } from 'src/app/core/services/utelize/general/chart-config-service';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import { OurDate } from 'src/app/core/interfaces/general/our-date';
import { populateOurDate } from 'src/app/core/functions-generic/functions-generic-date';
import { TippyToolTipService } from 'src/app/core/services/utelize/general/tippy-tool-tip-service';
import { WidgetCompanyTotalUsageDTO } from 'src/app/core/models/system/widget/widget-company-total-usage.dto';
import { WidgetIccidTotalUsageDTO } from 'src/app/core/models/system/widget/widget-Iccid-total-usage.dto';
import { WidgetService } from 'src/app/core/services/utelize/general/widget-service';

@Component({
  selector: 'app-chart-usage-per-day',
  templateUrl: './chart-usage-per-day.component.html',
  styleUrls: ['./chart-usage-per-day.component.scss'],
  providers: [TippyToolTipService]
})

export class ChartUsagePerDayComponent implements AfterViewInit, OnDestroy, OnInit {
  
  //CRN_20251209 - Use this either at Company Level or SIM Level

  @Input() allowContainerClose: boolean | undefined;
  @Input() chartHeight        : number  | undefined;
  @Input() chartTitle         : string  | undefined;
  @Input() containerGrow      : boolean | undefined;
  @Input() evenDaysOnly       : boolean | undefined;
  @Input() marginBottom       : string  | undefined="mb-0";
  @Input() showTitleHintText  : boolean | undefined;
  
  @Output() containerClosed         : EventEmitter<void>=new EventEmitter<void>();
  @Output() eSimManagerFormCancelled: EventEmitter<void>=new EventEmitter<void>();
  
  @ViewChild('chartRef') chartRef!: ChartComponent;
  
  private buildIccidTotalUsageChart: boolean=true;
  private filterCompanyID          : number       | undefined;
  private filterDate               : OurDate      | undefined=populateOurDate(this.exceptionsService, Date.now());
  private iccid                    : string       | undefined;
  private subIccidTotalUsage!      : Subscription | undefined;
  
  public expandContainer : boolean=true;
  public iccidChartUsage : any | undefined;
  public noData          : boolean=false;
  public visibleContainer: boolean=true;

  constructor(
    private chartConfigService : ChartConfigService,
    private exceptionsService  : ExceptionsService,
    private tippyToolTipService: TippyToolTipService,
    private widgetService      : WidgetService
  ) {

  }

  public ngOnInit(): void {
    try {
      if (this.chartHeight==undefined)
        this.chartHeight=250;

      if (this.chartTitle==undefined)
        this.chartTitle="Usage Per Day";
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngAfterViewInit(): void {
    try {
      this.buildIccidTotalUsageChart=true;
      
      this.tippyToolTipService.setupBootstrapTooltips('[data-bs-toggle="tooltip"]', 'left');
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public ngOnDestroy(): void {
    try {
      this.unsubscribeIccidTotalUsage();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public getCompanyTotalUsage(filterCompanyID: number | undefined, filterDate: OurDate | undefined) {
    try {
      if (filterCompanyID==undefined)
        throw new Error("ChartUsagePerDayComponent - CompanyID is undefined");

      this.filterCompanyID=filterCompanyID;
      this.filterDate     =filterDate;

      this.getTotalUsage();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public getIccidTotalUsage(iccid: string | undefined) {
    try {
      if (iccid==undefined)
        throw new Error("ChartUsagePerDayComponent - ICCID is undefined");

      this.iccid=iccid;

      this.getTotalUsage();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private getTotalUsage() {
    try {
      this.unsubscribeIccidTotalUsage();
      this.subIccidTotalUsage=new Subscription();

      const companyID  =<number>(this.filterCompanyID   ?? 0);
      const iccid      =this.iccid                      ?? "";
      const year       =<number>(this.filterDate?.year  ?? 1);
      const month      =<number>(this.filterDate?.month ?? 1);
      const day        =<number>(this.filterDate?.day   ?? 1);
      //CRN_20261013 - Retired
      // const sourceUsage=this.iccid
      //                   ? this.widgetService.getIccidTotalUsage  (iccid,     year, month, day)
      //                   : this.widgetService.getCompanyTotalUsage(companyID, year, month, day);
      //
      // const sourceUsageSubscription=sourceUsage
      // .pipe(
      //   catchError(error =>
      //     throwError(() => new Error(this.exceptionsService.formatHttpError(error)))
      //   )
      // )
      // .subscribe({
      //   next : data => this.updateIccidChart(data),
      //   error: error => this.exceptionsService.handleException(new Error(this.exceptionsService.formatHttpError(error)))
      // });
      //
      // this.subIccidTotalUsage.add(sourceUsageSubscription);

      const sourceUsageUpdates=this.iccid
                               ? this.widgetService.getIccidTotalUsageUpdates  (iccid,     year, month, day)
                               : this.widgetService.getCompanyTotalUsageUpdates(companyID, year, month, day);
  
      const sourceUsageUpdatesSubscription=sourceUsageUpdates
      .pipe(
        catchError(error =>
          throwError(() => new Error(this.exceptionsService.formatHttpError(error)))
        )
      )
      .subscribe({
        next : data => this.updateIccidChart(data),
        error: error => this.exceptionsService.handleException(new Error(error))
      });

      this.subIccidTotalUsage.add(sourceUsageUpdatesSubscription);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private updateIccidChart(data: WidgetCompanyTotalUsageDTO[] | WidgetIccidTotalUsageDTO[]) {
    try {
      this.noData=(data ?? []).length===0;

      if (this.noData) {
        this.buildIccidTotalUsageChart=true;
        this.iccidChartUsage          =undefined;
        return;
      }

      const newAccumulative=data.map(d => d.accumulativeUsage);
      const newDays        =data.map(d => !this.evenDaysOnly || d.dayOfMonth % 2===0 ? String(d.dayOfMonth) : "");
      const newTotal       =data.map(d => d.totalUsage);

      if (this.buildIccidTotalUsageChart) {
        this.buildIccidTotalUsageChart=false;
        
        //this.iccidTotalUsageForCurrentMonthChart('["--vz-danger", "--vz-success"]');
        this.iccidTotalUsageForCurrentMonthChart('["--vz-suresim-blue", "--vz-success"]');

        this.iccidChartUsage.plotOptions     =this.buildPlotOptions(newDays.length);
        this.iccidChartUsage.series[0].data  =newTotal;
        this.iccidChartUsage.series[1].data  =newAccumulative;
        this.iccidChartUsage.xaxis.categories=newDays;
        
        return;
      }

      if (this.chartRef?.updateSeries) {
        this.chartRef.updateSeries([
          {
            name: 'Total Usage MB',
            data: newTotal,
            type: 'column'
          },
          {
            name: 'Accumulative Usage MB',
            data: newAccumulative,
            type: 'line'
          }
        ], true);
    
        this.chartRef.updateOptions({
          chart: {
            zoom: { enabled: false },
            pan : { enabled: false }
          },
          plotOptions: this.buildPlotOptions(newDays.length),
          xaxis      : {
            categories: newDays
          }
        }, false);
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
      
  public unsubscribeIccidTotalUsage() {
    try {
      if (this.subIccidTotalUsage) {
        this.subIccidTotalUsage.unsubscribe();
        this.subIccidTotalUsage=undefined;
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
    
  private iccidTotalUsageForCurrentMonthChart(colors: any) {
    try {
      colors=this.chartConfigService.resolveColours(colors);
           
      this.iccidChartUsage={
        chart: {
          id     : 'iccid-usage-chart',
          height : this.chartHeight,
          pan: {
            enabled: false
          },
          toolbar: {
              show: false,
          },
          type: 'line',
          zoom: {
            enabled: false
          },
        },
        colors: colors,
        // plotOptions: {
        //   bar: {
        //     columnWidth: '90%'
        //   }
        // },
        plotOptions: this.buildPlotOptions(0),
        series     : [{
          name: 'Total Usage MB',
          data: [],
          type: 'column'
        }, {
          name: 'Accumulative Usage MB',
          data: [],
          type: 'line'
        }],
        stroke: {
          curve: 'smooth',
          width: [0, 3]
        },
        title: {
          style: {
            fontWeight: 600,
          },
          text: undefined
        },
        xaxis: {
          type      : 'string',
          categories: []
        },
        yaxis: [{
          title: {
            style: {
              fontWeight: 600,
            },
            text: 'Total Usage'
          },
        }, {
          min: 0,
          opposite: true,
          title   : {
            style: {
              fontWeight: 600,
            },
            text: 'Accumulative Usage'
          }
        }]
      };
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private buildPlotOptions(barCount: number): any {
    try {
      return {
        bar: {
          borderRadius           : 4,
          borderRadiusApplication: 'end',
          columnWidth            : barCount>12 ? '60%' : '40px'
        }
      };
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return { bar: { columnWidth: '60%' } };
    }
  }
  
  public toggleContainerExpansion(): void {
    try {
      this.expandContainer=!this.expandContainer;

      if (this.expandContainer) {
        setTimeout(() => {
          try {
            window.dispatchEvent(new Event('resize'));
          }
          catch(e) {
            this.exceptionsService.handleException(<Error>e);
          }
        }, 0);
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public collapseContainer(): void {
    try {
      this.expandContainer=false;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public closeContainer(): void {
    try {
      this.visibleContainer=false;
      this.containerClosed.emit();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
}
